import { transformBenefitsForSave } from "@platform/btx-plan-designer";
import { proposalQueryKeys } from "@platform/mpx-quotes-grid";
import {
  useMutation,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import { useCallback, useRef } from "react";
import { RateGuaranteeTypeEnum } from "@platform/openapi-types";

import { PLACEHOLDER_PLAN_ID_PREFIX } from "../utils/quotesPlanIds";

import type { ProposalPlanLevelFields } from "./useMaterializeProposalMutation";
import type {
  LocCoverageType,
  RfpBenefitPlan,
  RfpCoverage,
} from "@platform/btx-plan-designer";
import type {
  PadFillTarget,
  PlaceholderMaterializeTarget,
  Proposal,
  ProposalScope,
} from "@platform/mpx-quotes-grid";
import type { AxiosInstance } from "axios";

/** Request body for PATCH /btx/v1/proposal-plans/:id/benefits */
interface UpdateProposalPlanBenefitsRequest {
  benefits: RfpBenefitPlan["benefits"];
}

// Empty-string benefit values originate only from unedited grid seeds:
// `getValueWithDefault` seeds a field with "" when the carrier response has no
// value and the benefit has no default. A user-cleared field is written as
// `null`, never "". The backend rejects "" for numeric benefit fields
// (e.g. eliminationPeriodDays int64, idiPlanDesign.percent float64), so drop
// empty strings from the payload before saving — they carry no analyst intent.
const stripEmptyStrings = <T>(value: T): T => {
  if (Array.isArray(value)) {
    return value.map(stripEmptyStrings) as T;
  }

  if (value !== null && typeof value === "object") {
    const kept = Object.entries(value as Record<string, unknown>)
      .filter(([, fieldValue]) => fieldValue !== "")
      .map(
        ([key, fieldValue]) => [key, stripEmptyStrings(fieldValue)] as const,
      );

    return Object.fromEntries(kept) as T;
  }

  return value;
};

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === "object" && !Array.isArray(value);

const isObjectLike = (value: unknown): value is object =>
  value !== null && typeof value === "object";

const leafEqual = (a: unknown, b: unknown): boolean => {
  // Object.is handles primitives correctly, including NaN and distinct number
  // types that JSON.stringify collapses to "null". Only array leaves need a
  // structural compare (dropDefaultLeaves recurses into plain objects itself),
  // and JSON.stringify can throw (e.g. on bigint), so guard it.
  if (Object.is(a, b)) return true;
  if (!isObjectLike(a) || !isObjectLike(b)) return false;
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    return false;
  }
};

// Recursively drops every benefit leaf that is deep-equal to the same path in
// `baseline`, always keeping the `objectType` discriminator. Used to remove the
// to-API transform's structural defaults from a freshly created column.
const dropDefaultLeaves = (
  edited: Record<string, unknown>,
  baseline: Record<string, unknown>,
): Record<string, unknown> => {
  const result: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(edited)) {
    if (key === "objectType") {
      result[key] = value;
      continue;
    }

    const baseValue = baseline[key];

    if (isPlainObject(value)) {
      const pruned = dropDefaultLeaves(
        value,
        isPlainObject(baseValue) ? baseValue : {},
      );
      if (Object.keys(pruned).length > 0) result[key] = pruned;
    } else if (!leafEqual(value, baseValue)) {
      result[key] = value;
    }
  }

  return result;
};

// A newly filled column (a materialized placeholder or a pad-fill slot) starts
// with no benefits, so its grid row holds only the cells the analyst actually
// edited. The shared to-API transform, however, always stamps structural
// defaults (e.g. spouse/child `notIncluded`, `portability`, `ageBasis`) so a
// real-plan PATCH can overwrite prior data. Persisting those on a first edit
// prepopulates the whole column with "Not Included"/"None" (CD-1716).
// Transforming an empty grid row reproduces exactly those defaults, so we drop
// every benefit leaf equal to it, leaving just the analyst's edits. Only the
// new-plan paths need this — a real-plan PATCH still sends the full body.
const stripStructuralDefaults = (
  benefits: RfpBenefitPlan["benefits"],
  coverageType: LocCoverageType,
): RfpBenefitPlan["benefits"] => {
  if (!isPlainObject(benefits)) return benefits;

  const baseline = stripEmptyStrings(
    transformBenefitsForSave({}, coverageType) as RfpBenefitPlan["benefits"],
  );
  if (!isPlainObject(baseline)) return benefits;

  return dropDefaultLeaves(benefits, baseline) as RfpBenefitPlan["benefits"];
};

// Benefits to persist when an edit creates a brand-new column (a materialized
// placeholder or a pad-fill slot): strip the transform's structural defaults so
// the column isn't prepopulated (CD-1716). When coverageType is unknown the
// benefits were never transformed, so pass them through unchanged.
const benefitsForNewColumn = (
  benefits: RfpBenefitPlan["benefits"],
  coverageType: LocCoverageType | undefined,
): RfpBenefitPlan["benefits"] =>
  coverageType ? stripStructuralDefaults(benefits, coverageType) : benefits;

const patchProposalPlanBenefits = async (
  axiosInstance: AxiosInstance,
  planId: string,
  benefits: RfpBenefitPlan["benefits"],
): Promise<void> => {
  const body: UpdateProposalPlanBenefitsRequest = { benefits };
  await axiosInstance.patch(`btx/v1/proposal-plans/${planId}/benefits`, body);
};

const patchProposalPlanFields = async (
  axiosInstance: AxiosInstance,
  planId: string,
  fields: ProposalPlanLevelFields,
): Promise<void> => {
  await axiosInstance.patch(`btx/v1/proposal-plans/${planId}`, fields);
};

// Builds the plan-level fields (siblings of `benefits`) for the PATCH, omitting
// empty/undefined so a blank never clobbers a saved value (notably fundingType, an
// enum where "" is never valid). The exceptions are fields the analyst cleared
// on purpose: a cap with no rate guarantee, and a Plan Name. `rateCap` maps from
// the FE's `rateCap`.
const buildPlanLevelFields = (
  plan: RfpBenefitPlan,
  planId: string,
  editedFields: Set<string>,
): ProposalPlanLevelFields => {
  const fields: ProposalPlanLevelFields = {};

  if (plan.fundingType) fields.fundingType = plan.fundingType;

  if (plan.participationRequirements)
    fields.participationRequirements = plan.participationRequirements;

  if (plan.rateGuarantee) fields.rateGuarantee = plan.rateGuarantee;

  if (plan.rateCap) fields.rateCap = plan.rateCap;
  else if (editedFields.has(RATE_GUARANTEE_TYPE_KEY) && plan.rateCap === "")
    fields.rateCap = "";

  if (editedFields.has("eligibilityClasses"))
    fields.eligibilityClasses = plan.eligibilityClasses;

  if (editedFields.has("optionName")) fields.optionName = plan.optionName;

  // Sent as "" when cleared, so the analyst can remove a carrier's plan name.
  if (editedFields.has("name")) fields.name = plan.name ?? "";

  return fields;
};

const hasPlanLevelField = (fields: ProposalPlanLevelFields): boolean =>
  Object.keys(fields).length > 0;

// Rate Guarantee / Rate Caps is presented in the grid as a benefit-body field
// trio (rateGuaranteeType/Amount/Cap) but persisted at plan level as
// `rateGuarantee: {type, amount}` + `rateCap`. These keys are the grid-facing
// benefit keys, not benefit-body fields the backend accepts on /benefits.
const RATE_GUARANTEE_TYPE_KEY = "rateGuaranteeType";
const RATE_GUARANTEE_AMOUNT_KEY = "rateGuaranteeAmount";
const RATE_GUARANTEE_CAP_KEY = "rateGuaranteeCap";
// benefitPlanLevel fields (see onCellValueChanged's isBenefitPlanLevelField
// branch) stamp _dirtyCells with their grid benefitKey too, so they must be
// excluded here the same way — otherwise hasBenefitEdit below wrongly treats
// a plan-level-only edit as a benefits-body edit and fires an extra PATCH.
const PLAN_LEVEL_BENEFIT_KEYS = new Set([
  RATE_GUARANTEE_TYPE_KEY,
  RATE_GUARANTEE_AMOUNT_KEY,
  RATE_GUARANTEE_CAP_KEY,
  "eligibilityClasses",
  "optionName",
  "name",
]);

// Lifts the edited rate-guarantee grid values off the benefit body onto the
// plan, so buildPlanLevelFields routes them to the plan-fields PATCH. The
// whitelist benefits transform already omits them from the /benefits PATCH,
// so no delete from the benefits body is needed. Only runs when the analyst
// actually edited the field (its type row stamps _dirtyCells).
const syncRateGuaranteeToPlanLevel = (
  plan: RfpBenefitPlan,
  editedPlanFields: Set<string>,
): void => {
  if (!editedPlanFields.has(RATE_GUARANTEE_TYPE_KEY)) return;

  const benefits = plan.benefits as Record<string, unknown> | undefined;
  const type = benefits?.[RATE_GUARANTEE_TYPE_KEY];
  const amount = benefits?.[RATE_GUARANTEE_AMOUNT_KEY];
  const cap = benefits?.[RATE_GUARANTEE_CAP_KEY];

  const typeHasNoCap =
    typeof type === "string" &&
    (type === RateGuaranteeTypeEnum.notIncluded ||
      type === RateGuaranteeTypeEnum.notOffered);

  if (typeof type === "string" && type !== "") {
    const rateGuaranteeAmount = (!typeHasNoCap ? amount : 0) as number;
    plan.rateGuarantee = {
      type: type as NonNullable<RfpBenefitPlan["rateGuarantee"]>["type"],
      amount: rateGuaranteeAmount,
    };
  }

  if (typeHasNoCap) {
    plan.rateCap = "";
  } else if (typeof cap === "string" && cap !== "") {
    plan.rateCap = cap;
  }
};

// FE-only control fields that are never sent to carriers — e.g. Critical
// Illness's "Dependent Benefits Included?" toggle, which only reveals the
// spouse/child rows. Voluntary Life splits this into separate spouse and child
// toggles. An edit to one of these must not trigger a benefits PATCH: the
// transform drops the field and would persist the dependents as notIncluded,
// then the refetch flips the toggle back off.
const FE_ONLY_BENEFIT_KEYS = new Set([
  "dependentBenefitsIncluded",
  "spouseBenefitsIncluded",
  "childBenefitsIncluded",
]);

// Inverse of buildPlanLevelFields: maps the API field payload back onto the plan
// shape for the optimistic cache write. `rateCap` is the FE's `rateCap`.
const planLevelFieldsCachePatch = (
  fields: ProposalPlanLevelFields,
): Partial<RfpBenefitPlan> => {
  const patch: Partial<RfpBenefitPlan> = {};
  if (fields.fundingType !== undefined) patch.fundingType = fields.fundingType;
  if (fields.participationRequirements !== undefined)
    patch.participationRequirements = fields.participationRequirements;
  if (fields.rateGuarantee !== undefined)
    patch.rateGuarantee = fields.rateGuarantee;
  if (fields.rateCap !== undefined) patch.rateCap = fields.rateCap;
  if (fields.eligibilityClasses !== undefined)
    patch.eligibilityClasses = fields.eligibilityClasses;
  if (fields.name !== undefined) patch.name = fields.name;
  return patch;
};

// Reads the pre-edit value of exactly the plan-level fields about to change,
// so a failed PATCH rolls back only those fields on this plan — never a
// whole-cache snapshot that would also undo a concurrent sibling edit.
const readPlanLevelFieldsPatch = (
  plan: RfpBenefitPlan | undefined,
  fields: ProposalPlanLevelFields,
): Partial<RfpBenefitPlan> => {
  if (!plan) return {};
  const patch: Partial<RfpBenefitPlan> = {};
  if (fields.fundingType !== undefined) {
    patch.fundingType = plan.fundingType;
  }
  if (fields.participationRequirements !== undefined) {
    patch.participationRequirements = plan.participationRequirements;
  }
  if (fields.rateGuarantee !== undefined) {
    patch.rateGuarantee = plan.rateGuarantee;
  }
  if (fields.rateCap !== undefined) {
    patch.rateCap = plan.rateCap;
  }
  if (fields.eligibilityClasses !== undefined) {
    patch.eligibilityClasses = plan.eligibilityClasses;
  }
  if (fields.name !== undefined) {
    patch.name = plan.name;
  }
  return patch;
};

// Patches a single plan by id in place, leaving every other plan untouched —
// used for both the immediate optimistic write and surgical rollback, so a
// failure on one plan can never stomp a concurrent edit to a different plan.
const patchPlanInCache = (
  queryClient: QueryClient,
  queryKey: ReturnType<typeof proposalQueryKeys.scoped>,
  planId: string,
  patch: Partial<RfpBenefitPlan>,
): void => {
  queryClient.setQueryData<Proposal[]>(queryKey, (old) => {
    if (!old) return old;
    return old.map((proposal) => ({
      ...proposal,
      coverages: proposal.coverages.map((coverage) => ({
        ...coverage,
        planGroups: coverage.planGroups.map((group) => ({
          ...group,
          plans: group.plans.map((plan) =>
            plan.id === planId ? { ...plan, ...patch } : plan,
          ),
        })),
      })),
    }));
  });
};

const findPlanInCache = (
  queryClient: QueryClient,
  queryKey: ReturnType<typeof proposalQueryKeys.scoped>,
  planId: string,
): RfpBenefitPlan | undefined => {
  const proposals = queryClient.getQueryData<Proposal[]>(queryKey) ?? [];
  const allPlans = proposals.flatMap((proposal) =>
    proposal.coverages.flatMap((coverage) =>
      coverage.planGroups.flatMap((group) => group.plans),
    ),
  );
  return allPlans.find((plan) => plan.id === planId) as unknown as
    | RfpBenefitPlan
    | undefined;
};

interface UseProposalPlanBenefitsMutationOptions {
  rfpId: string;
  axiosInstance: AxiosInstance;
  /** The proposals read scope (ADR-031) currently rendered in the grid — the
   * optimistic cache write must target this exact scoped query, not the
   * unscoped `.all` key, since `setQueryData`/`getQueryData` require an exact
   * key match (unlike `invalidateQueries`, which prefix-matches every scope). */
  scope: ProposalScope;
  coverageType: LocCoverageType | undefined;
  /**
   * Placeholder plan id → materialize keys. When a dirty plan id is present here
   * the edit materializes a real analyst proposal instead of PATCHing a plan that
   * does not exist in the database. Absent for non-placeholder grids.
   */
  placeholderMaterializeByPlanId?: Map<string, PlaceholderMaterializeTarget>;
  /**
   * Pad plan id → fill keys (CD-1612 partial fill). When a dirty plan id is
   * present here the edit adds a real plan to the existing under-quoted group
   * instead of PATCHing a plan that does not exist in the database.
   */
  padFillByPlanId?: Map<string, PadFillTarget>;
  /**
   * Ids of real (persisted) plans that currently hold no benefits — an unfilled
   * slot of a materialized multi-plan group or a plan added via "Add Plan".
   * Their first benefit edit takes the PATCH path but must strip the transform's
   * structural defaults so the column isn't prepopulated (CD-1716), just like a
   * materialized/pad-filled column.
   */
  emptyBenefitPlanIds?: Set<string>;
  /**
   * Ids of the plans behind the Incumbent/Requested column. They are the RFP's
   * own benefit plans, so their edits (extended fields only — CD-2301) go to
   * the benefit-plan endpoint via `saveRequestedPlanBenefits`, not to the
   * proposal-plan endpoints this hook otherwise PATCHes.
   */
  requestedBenefitPlanIds?: Set<string>;
  saveRequestedPlanBenefits?: (plan: RfpBenefitPlan) => void;
  onSaveError?: (err: Error) => void;
  materialize: (
    target: PlaceholderMaterializeTarget,
    benefits: RfpBenefitPlan["benefits"],
    planFields: ProposalPlanLevelFields,
    footnote?: string,
    onMaterialized?: (planId: string) => void,
  ) => void;
  addPlanToGroup: (
    target: PadFillTarget,
    benefits: RfpBenefitPlan["benefits"],
    planFields: ProposalPlanLevelFields,
    onAdded?: (planId: string) => void,
  ) => void;
  /**
   * Fires once a pad-fill or materialize has created the real plan behind an
   * edited synthetic column, with the id that column's cells now belong to.
   * Anything keyed by plan id (the "Edited" cell review) has to wait for it:
   * the synthetic `placeholder-` id has no row and the BFF rejects it.
   */
  onNewPlanPersisted?: (realPlanId: string, cells: DirtyCell[]) => void;
}

type DirtyCell = NonNullable<RfpCoverage["_dirtyCells"]>[number];

interface BenefitsMutationVariables {
  planId: string;
  benefits: RfpBenefitPlan["benefits"];
}

/** The edits of one run, waiting to be saved: each plan as its last edit left it. */
interface PendingSave {
  plansById: ReadonlyMap<string, RfpBenefitPlan>;
  dirtyCells: NonNullable<RfpCoverage["_dirtyCells"]>;
}

const dirtyPlansOf = (coverage: RfpCoverage): [string, RfpBenefitPlan][] =>
  [...new Set(coverage._dirtyPlanIds ?? [])].flatMap(
    (planId): [string, RfpBenefitPlan][] => {
      const plan = coverage.benefitPlans?.find((p) => p.id === planId);
      return plan ? [[planId, plan]] : [];
    },
  );

interface BenefitsMutationContext {
  originalBenefits: RfpBenefitPlan["benefits"] | undefined;
  // Captured at onMutate time so rollback targets the scope that was actually
  // patched — the hook's `scope` prop (and thus the outer `scopedQueryKey`
  // closure) can change while this mutation is still in flight (e.g. toggling
  // quote history), which would otherwise roll back into the wrong cache entry.
  scopedQueryKey: ReturnType<typeof proposalQueryKeys.scoped>;
}

interface FieldsMutationVariables {
  planId: string;
  fields: ProposalPlanLevelFields;
}

interface FieldsMutationContext {
  originalFieldsPatch: Partial<RfpBenefitPlan>;
  scopedQueryKey: ReturnType<typeof proposalQueryKeys.scoped>;
}

export const useProposalPlanBenefitsMutation = ({
  rfpId,
  axiosInstance,
  scope,
  coverageType,
  placeholderMaterializeByPlanId,
  padFillByPlanId,
  emptyBenefitPlanIds,
  requestedBenefitPlanIds,
  saveRequestedPlanBenefits,
  onSaveError,
  materialize,
  addPlanToGroup,
  onNewPlanPersisted,
}: UseProposalPlanBenefitsMutationOptions): {
  onCoverageChange: (updatedCoverage: RfpCoverage) => void;
} => {
  const queryClient = useQueryClient();

  // `.all` matches every scoped variant for cancel/invalidate (React Query
  // prefix-matches queryKey arrays); the optimistic read/write below needs
  // the exact scoped key instead, since it renders the grid's live data.
  const queryKey = proposalQueryKeys.all(rfpId);
  const scopedQueryKey = proposalQueryKeys.scoped(rfpId, scope);

  const { mutate } = useMutation({
    mutationFn: ({ planId, benefits }: BenefitsMutationVariables) =>
      patchProposalPlanBenefits(axiosInstance, planId, benefits),
    onMutate: async ({
      planId,
      benefits,
    }: BenefitsMutationVariables): Promise<BenefitsMutationContext> => {
      // Cancel in-flight refetches so they don't overwrite the optimistic
      // update, then capture the pre-edit value for a surgical rollback
      // before overwriting it.
      await queryClient.cancelQueries({ queryKey });
      const originalBenefits = findPlanInCache(
        queryClient,
        scopedQueryKey,
        planId,
      )?.benefits;
      patchPlanInCache(queryClient, scopedQueryKey, planId, { benefits });
      return { originalBenefits, scopedQueryKey };
    },
    onSuccess: () => {
      // Mark stale without forcing an immediate refetch: an active refetch
      // here would re-fetch and rebuild the entire SBS grid after every
      // single cell edit, and could race ahead of a sibling plan's still
      // in-flight save. The cache already shows the edited value; the next
      // natural refetch (LOC switch, remount, window refocus) resyncs it.
      queryClient.invalidateQueries({ queryKey, refetchType: "none" });
    },
    onError: (
      err: Error,
      { planId }: BenefitsMutationVariables,
      context: BenefitsMutationContext | undefined,
    ) => {
      // Roll back only this plan's benefits — a whole-cache snapshot would
      // also undo a concurrent sibling plan's (or this plan's own
      // plan-level fields') still-in-flight optimistic edit.
      if (context) {
        patchPlanInCache(queryClient, context.scopedQueryKey, planId, {
          benefits: context.originalBenefits,
        });
      }
      onSaveError?.(err);
    },
  });

  // Plan-level field edits (funding type, etc.) on a real plan go to the
  // dedicated plan-fields endpoint. Placeholders carry them in the materialize
  // payload instead (see below).
  const { mutate: mutateFields } = useMutation({
    mutationFn: ({ planId, fields }: FieldsMutationVariables) =>
      patchProposalPlanFields(axiosInstance, planId, fields),
    onMutate: async ({
      planId,
      fields,
    }: FieldsMutationVariables): Promise<FieldsMutationContext> => {
      await queryClient.cancelQueries({ queryKey });
      const originalFieldsPatch = readPlanLevelFieldsPatch(
        findPlanInCache(queryClient, scopedQueryKey, planId),
        fields,
      );
      patchPlanInCache(
        queryClient,
        scopedQueryKey,
        planId,
        planLevelFieldsCachePatch(fields),
      );
      return { originalFieldsPatch, scopedQueryKey };
    },
    onSuccess: () => {
      // Mark stale without a background refetch: a full refetch here can race
      // with a sibling plan's benefits PATCH and overwrite its optimistic
      // state (notably Dental PPO co-plans). The grid already shows the edited
      // value; the next natural refetch syncs the server copy.
      queryClient.invalidateQueries({ queryKey, refetchType: "none" });
    },
    onError: (
      err: Error,
      { planId }: FieldsMutationVariables,
      context: FieldsMutationContext | undefined,
    ) => {
      if (context?.originalFieldsPatch) {
        patchPlanInCache(
          queryClient,
          context.scopedQueryKey,
          planId,
          context.originalFieldsPatch,
        );
      }
      onSaveError?.(err);
    },
  });

  // ag-grid can keep invoking an onCellValueChanged that closed over an earlier
  // render's callback after the analyst switches coverage type. Read coverageType
  // and the placeholder map through refs so the transform always stamps the
  // correct objectType and the edit routes to the right save path.
  const coverageTypeRef = useRef(coverageType);
  coverageTypeRef.current = coverageType;
  const placeholderMapRef = useRef(placeholderMaterializeByPlanId);
  placeholderMapRef.current = placeholderMaterializeByPlanId;
  const padFillMapRef = useRef(padFillByPlanId);
  padFillMapRef.current = padFillByPlanId;
  const emptyBenefitPlanIdsRef = useRef(emptyBenefitPlanIds);
  emptyBenefitPlanIdsRef.current = emptyBenefitPlanIds;
  const requestedBenefitPlanIdsRef = useRef(requestedBenefitPlanIds);
  requestedBenefitPlanIdsRef.current = requestedBenefitPlanIds;
  const saveRequestedPlanBenefitsRef = useRef(saveRequestedPlanBenefits);
  saveRequestedPlanBenefitsRef.current = saveRequestedPlanBenefits;
  const onNewPlanPersistedRef = useRef(onNewPlanPersisted);
  onNewPlanPersistedRef.current = onNewPlanPersisted;

  // Persists one dirty plan's edits, routing to the RFP-side save, a
  // pad/placeholder materialize, or a proposal-plan PATCH as appropriate.
  // Extracted from onCoverageChange to keep that loop's branching readable.
  const saveDirtyPlan = useCallback(
    (
      plan: RfpBenefitPlan,
      planId: string,
      dirtyCells: NonNullable<RfpCoverage["_dirtyCells"]>,
      currentCoverageType: LocCoverageType | undefined,
      placeholderMap: Map<string, PlaceholderMaterializeTarget> | undefined,
      padFillMap: Map<string, PadFillTarget> | undefined,
      emptyBenefitPlanIds: Set<string> | undefined,
    ) => {
      // The Incumbent/Requested column's plans are the RFP's own benefit
      // plans; they have no proposal_plan row to PATCH.
      if (requestedBenefitPlanIdsRef.current?.has(planId)) {
        saveRequestedPlanBenefitsRef.current?.(plan);
        return;
      }

      // Transform from grid format (transformFromApi output) back to API format
      // before sending to the backend — mirrors how the RFP-side autosave works.
      // Skip transform if coverageType is unknown (send as-is).
      // A throw here (e.g. a months type with no months) runs in a microtask,
      // so surface it instead of letting it reach only the console.
      let apiFormatBenefits: RfpBenefitPlan["benefits"];
      try {
        apiFormatBenefits = stripEmptyStrings(
          (currentCoverageType
            ? transformBenefitsForSave(plan.benefits, currentCoverageType)
            : plan.benefits) as RfpBenefitPlan["benefits"],
        );
      } catch (error: unknown) {
        onSaveError?.(
          error instanceof Error ? error : new Error(String(error)),
        );
        return;
      }

      // Plan-level fields (funding type, etc.) are siblings of `benefits` on the
      // plan; the benefitPlanLevel cell-edit path writes them directly onto it.
      const editedPlanFields = new Set(
        dirtyCells
          .filter((cell) => cell.planId === planId)
          .map((cell) => cell.benefitKey),
      );
      // Rate Guarantee edits present as benefit cells but persist at plan
      // level — move them onto the plan before serializing plan fields.
      syncRateGuaranteeToPlanLevel(plan, editedPlanFields);
      const planFields = buildPlanLevelFields(plan, planId, editedPlanFields);

      // A pad plan (empty slot on a real under-quoted group, CD-1612) has no
      // real row to PATCH: add a real plan to the existing group carrying
      // this edit. A placeholder plan materializes a whole analyst proposal
      // instead. Real plans take the PATCH path(s).
      const padFillTarget = padFillMap?.get(planId);
      const materializeTarget = placeholderMap?.get(planId);
      const planCells = dirtyCells.filter((cell) => cell.planId === planId);
      const onPersisted = (realPlanId: string): void =>
        onNewPlanPersistedRef.current?.(realPlanId, planCells);
      if (padFillTarget) {
        // A pad or placeholder plan is a brand-new column; persist only the
        // analyst's edits, never the transform's structural defaults (CD-1716).
        addPlanToGroup(
          padFillTarget,
          benefitsForNewColumn(apiFormatBenefits, currentCoverageType),
          planFields,
          onPersisted,
        );
        return;
      }
      if (materializeTarget) {
        materialize(
          materializeTarget,
          benefitsForNewColumn(apiFormatBenefits, currentCoverageType),
          planFields,
          undefined,
          onPersisted,
        );
        return;
      }
      if (planId.startsWith(PLACEHOLDER_PLAN_ID_PREFIX)) {
        // A placeholder plan has no real proposal_plan row to PATCH. When it
        // is materializable it takes the branch above; otherwise there is no
        // persistable target, so skip rather than PATCH a non-UUID id (400).
        return;
      }

      // Only PATCH benefits when a benefit cell actually changed — a benefit
      // edit stamps _dirtyCells, a plan-level-only edit (funding type, etc.)
      // does not. Without this guard a plan-level-only edit sends an empty
      // benefits body ({}) and the benefits endpoint 400s on the required field.
      const hasBenefitEdit = dirtyCells.some(
        (cell) =>
          cell.planId === planId &&
          !FE_ONLY_BENEFIT_KEYS.has(cell.benefitKey) &&
          !PLAN_LEVEL_BENEFIT_KEYS.has(cell.benefitKey),
      );
      if (hasBenefitEdit) {
        // The first benefit edit of a real plan that is still empty (an
        // unfilled slot of a materialized group, or a plan added via "Add
        // Plan") behaves like a new column: strip the transform's structural
        // defaults so untouched cells aren't prepopulated (CD-1716). Once the
        // plan holds data it leaves the set, so deliberate values (including
        // "Not Included") on subsequent edits are preserved.
        const benefits = emptyBenefitPlanIds?.has(planId)
          ? benefitsForNewColumn(apiFormatBenefits, currentCoverageType)
          : apiFormatBenefits;
        mutate({ planId, benefits });
      }
      if (hasPlanLevelField(planFields)) {
        mutateFields({ planId, fields: planFields });
      }
    },
    [mutate, mutateFields, materialize, addPlanToGroup, onSaveError],
  );

  // A fill-handle drag or multi-cell paste reaches here as a run of cell
  // edits in one task. Saving each as it arrives puts several full-body
  // PATCHes for the same plan in flight at once, and whichever lands last
  // wins. Collect the run and save each plan once, from its last edit, when
  // the run is over.
  const pendingSaveRef = useRef<PendingSave | null>(null);

  const savePending = useCallback(() => {
    const pending = pendingSaveRef.current;
    pendingSaveRef.current = null;
    if (!pending) return;

    for (const [planId, plan] of pending.plansById) {
      saveDirtyPlan(
        plan,
        planId,
        pending.dirtyCells,
        coverageTypeRef.current,
        placeholderMapRef.current,
        padFillMapRef.current,
        emptyBenefitPlanIdsRef.current,
      );
    }
  }, [saveDirtyPlan]);

  const onCoverageChange = useCallback(
    (updatedCoverage: RfpCoverage) => {
      const pending = pendingSaveRef.current;
      if (!pending) queueMicrotask(savePending);

      pendingSaveRef.current = {
        plansById: new Map([
          ...(pending?.plansById ?? []),
          ...dirtyPlansOf(updatedCoverage),
        ]),
        dirtyCells: [
          ...(pending?.dirtyCells ?? []),
          ...(updatedCoverage._dirtyCells ?? []),
        ],
      };
    },
    [savePending],
  );

  return { onCoverageChange };
};
